<template>
  <div
    class="w-full container-EXPONDO"
    :class="bannerTextColor"
  >
    <div
      class="py-1.5 text-center flex items-center h-full"
    >
      <button
        v-if="showNavButtons"
        class="flex justify-center"
        :aria-label="$t('Scroll left')"
        @click="$emit('click-prev')"
      >
        <span class="iconify svg--chevron-left" />
      </button>
      <div class="mx-4 flex-1">
        <CustomLink
          :url="bannerUrl"
          :type="LINK_TYPE.NoStyle"
          :is-block="true"
          @link-click="handleBannerClick"
        >
          <div
            class="flex items-center gap-4 lg:justify-center lg:text-center"
            :class="mobileAlignClasses"
          >
            <div class="gap-2 flex flex-row items-center">
              <div
                v-if="icon"
                class="flex items-center"
              >
                <span :class="icon" />
              </div>
              <div class="flex flex-col lg:flex-row">
                <DisplayHtmlDangerously
                  :html-text="bannerTextWithVariables"
                  class="text-xs"
                  :class="{'font-bold': bannerTextBold}"
                />
                <p
                  v-if="additionalBannerText"
                  class="text-xs lg:ml-1"
                  :class="[{ 'font-bold': additionalBannerTextBold }, additionalBannerTextColor]"
                >
                  {{ additionalBannerText }}
                </p>
              </div>
            </div>
            <div
              v-if="bannerButtonText"
              class="px-2 py-1 rounded flex text-xs justify-center text-center"
              :class="[{'font-bold': bannerButtonTextBold}, {'underline': bannerButtonTextUnderline},
                       bannerButtonTextColor, bannerButtonBackgroundColor]"
            >
              {{ bannerButtonText }}
            </div>
          </div>
        </CustomLink>
      </div>
      <button
        v-if="showNavButtons"
        class="flex justify-center"
        :aria-label="$t('Scroll right')"
        @click="$emit('click-next')"
      >
        <span class="iconify svg--chevron-right" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">

import { LINK_TYPE } from '~~/types/components/Link/LinkType';

import { PROMO_CODE_TAG } from '~~/constants/regex';

export default defineComponent({
  name: 'BannerProposition',

  props: {
    pageCategory: {
      type: String,
      default: '',
    },
    campaignName: {
      type: String,
      required: true,
    },
    bannerText: {
      type: String,
      required: true,
    },
    bannerTextBold: {
      type: Boolean,
      default: true,
    },
    bannerTextColor: {
      type: String,
      default: 'text-EXPONDO-white',
    },
    additionalBannerText: {
      type: String,
      default: '',
    },
    additionalBannerTextColor: {
      type: String,
      default: '',
    },
    additionalBannerTextBold: {
      type: Boolean,
      default: false,
    },
    mobileContentAlign: {
      type: String,
      default: 'center',
      validator: (value: string) => {

        return ['left', 'center', 'right'].includes(value);
      
      },
    },
    bannerButtonText: {
      type: String,
      default: '',
    },
    bannerButtonTextBold: {
      type: Boolean,
      default: false,
    },
    bannerButtonTextUnderline: {
      type: Boolean,
      default: false,
    },
    bannerButtonTextColor: {
      type: String,
      default: 'text-EXPONDO-white',
    },
    bannerButtonBackgroundColor: {
      type: String,
      default: 'bg-EXPONDO-red',
    },
    icon: {
      type: String,
      default: '',
    },
    bannerUrl: {
      type: String,
      default: '',
    },
    showNavButtons: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click-next', 'click-prev'],
  setup(props) {

    const { trackClickPromoBanner } = useGtmEvents();
    const handleBannerClick = () => {

      trackClickPromoBanner({
        campaignName: props.campaignName,
        pageCategory: props.pageCategory,
      });
    
    };
    const mobileAlignClasses = computed(() => {

      switch (props.mobileContentAlign) {

        case 'left':
          return 'justify-start text-left';
        case 'right':
          return 'justify-end text-right';
        default:
          return 'justify-center text-center';
      
      }
    
    });

    const PROMO_CODE_CLASSES = `bg-EXPONDO-white text-EXPONDO-primary font-bold px-2 py-1
      rounded border border-dashed border-EXPONDO-primary inline-block`;

    const { resolvePlaceholders } = usePromoBannerPlaceholders();

    const bannerTextWithVariables = computed(() => resolvePlaceholders(props.bannerText)
      .replace(
        PROMO_CODE_TAG,
        (_, code) => `<span class="${PROMO_CODE_CLASSES}">${code}</span>`,
      ));

    return {
      LINK_TYPE,
      handleBannerClick,
      mobileAlignClasses,
      bannerTextWithVariables,
    };
  
  },
});

</script>
